/**
 * panels.ts — the left operation column and every right-hand tab panel.
 */
import { el, clear } from './dom';
import { fmt, type UIContext } from './context';
import { buyCard, bar, sectionTitle, sparkline } from './components';
import { floatText } from './animation';
import { format, formatRate, formatDuration } from '../core/numbers';
import {
  ORES,
  TOOLS,
  BUILDINGS,
  WORKERS,
  UPGRADES,
  TECHS,
  SKILLS,
  ACHIEVEMENTS,
  ABILITIES,
  NUGGET_BY_ID,
  ASCENSION_PERKS,
  MASTERY,
  MAX_MASTERY_TIER,
  CHALLENGES,
  ORE_BY_DEPTH,
  BALANCE,
} from '../data';
import * as G from '../systems';

// ───────────────────────────── Left: Operation ─────────────────────────────
export function buildOperation(ctx: UIContext, canvas: HTMLCanvasElement): HTMLElement {
  const s = () => ctx.engine.state;

  // Focus layer selector (rebuilt when depth changes).
  const focusRow = el('div', { class: 'focus-row' });
  const rebuildFocus = (): void => {
    clear(focusRow);
    for (let depth = 0; depth <= s().mining.maxDepth; depth++) {
      const ore = ORE_BY_DEPTH[depth]!;
      const b = el('button', {
        class: 'focus-btn',
        title: `${ore.name} (Tiefe ${depth})`,
        text: ore.icon,
        onClick: () => {
          G.setFocus(s(), depth);
          rebuildFocus();
        },
      });
      ctx.binder.toggle(b, 'active', () => s().mining.focusDepth === depth);
      focusRow.appendChild(b);
    }
  };
  rebuildFocus();
  ctx.bus.on('layer:unlocked', rebuildFocus);
  ctx.bus.on('reset', rebuildFocus);
  ctx.bus.on('load', rebuildFocus);

  // Big mine button.
  const clickGain = el('span', { class: 'mine-gain' });
  const mineBtn = el(
    'button',
    {
      class: 'mine-btn',
      attrs: { 'aria-label': 'Erz abbauen' },
      onClick: (e) => {
        const d = ctx.engine.derived();
        const added = G.clickMine(s(), d, ctx.bus);
        ctx.sfx.play('click');
        const me = e as MouseEvent;
        canvasBurst(canvas, me);
        if (added > 0) floatText(mineBtn, `+${format(added)}`, 'gain');
      },
    },
    el('span', { class: 'mine-icon', text: '⛏️' }),
    el('span', { text: 'ABBAU' }),
    clickGain,
  );
  ctx.binder.text(clickGain, () => `+${fmt(ctx, ctx.engine.derived().clickPower)} / Klick`);

  // Combo meter — shows the live click-combo multiplier + a draining timer bar.
  const comboMult = el('span', { class: 'combo-mult' });
  const comboFill = el('div', { class: 'combo-bar-fill' });
  const comboMeter = el('div', { class: 'combo-meter' },
    el('span', { class: 'combo-label', text: '🔥 COMBO' }),
    comboMult,
    el('div', { class: 'combo-bar' }, comboFill),
  );
  ctx.binder.effect(() => {
    const st = s();
    const stacks = G.comboStacks(st);
    comboMeter.classList.toggle('hidden', stacks <= 0);
    if (stacks <= 0) return;
    comboMult.textContent = `×${G.comboMultiplier(st).toFixed(2)}`;
    const elapsed = st.meta.gameTimeMs - st.combo.lastClickAt;
    const left = Math.max(0, 1 - elapsed / BALANCE.combo.window);
    comboFill.style.width = `${left * 100}%`;
  });

  // Ability bar — activatable cooldown boosts.
  const abilityBar = el('div', { class: 'ability-bar' });
  for (const a of ABILITIES) {
    const cdText = el('span', { class: 'ability-cd' });
    const btn = el('button', {
      class: 'ability-btn',
      title: `${a.name} — ${a.description}`,
      onClick: () => {
        if (G.activateAbility(s(), a.id, ctx.bus)) ctx.sfx.play('unlock');
        else ctx.sfx.play('error');
      },
    },
      el('span', { class: 'ability-icon', text: a.icon }),
      cdText,
    );
    ctx.binder.toggle(btn, 'hidden', () => !G.abilityUnlocked(s(), a.id));
    ctx.binder.toggle(btn, 'active', () => G.abilityActive(s(), a.id));
    ctx.binder.disabled(btn as HTMLButtonElement, () => !G.abilityReady(s(), a.id));
    ctx.binder.effect(() => {
      const st = s();
      const cd = G.abilityCooldownRemaining(st, a.id);
      const active = G.abilityActive(st, a.id);
      if (active) {
        cdText.textContent = `${Math.ceil(G.abilityActiveRemaining(st, a.id) / 1000)}s`;
        btn.style.setProperty('--cd', '0');
      } else if (cd > 0) {
        cdText.textContent = `${Math.ceil(cd / 1000)}`;
        btn.style.setProperty('--cd', String(cd / (a.cooldown * 1000)));
      } else {
        cdText.textContent = '';
        btn.style.setProperty('--cd', '0');
      }
    });
    abilityBar.appendChild(btn);
  }

  // Tool upgrade.
  const toolName = el('span', { class: 'tool-name' });
  const toolBtn = el('button', {
    class: 'op-btn',
    onClick: () => (G.upgradeTool(s(), ctx.bus) ? ctx.sfx.play('buy') : ctx.sfx.play('error')),
  });
  ctx.binder.text(toolName, () => {
    const t = TOOLS[s().mining.toolLevel - 1];
    return t ? `${t.icon} ${t.name}` : 'Werkzeug max.';
  });
  ctx.binder.text(toolBtn, () => {
    const cost = G.nextToolCost(s());
    return cost === null ? 'Werkzeug max.' : `Werkzeug verbessern — $${format(cost)}`;
  });
  ctx.binder.disabled(toolBtn as HTMLButtonElement, () => {
    const cost = G.nextToolCost(s());
    return cost === null || s().economy.money < cost;
  });

  // Depth unlock.
  const depthBtn = el('button', {
    class: 'op-btn depth-btn',
    onClick: () => (G.unlockNextDepth(s(), ctx.bus) ? ctx.sfx.play('unlock') : ctx.sfx.play('error')),
  });
  ctx.binder.text(depthBtn, () => {
    const cost = G.nextDepthCost(s());
    if (cost === null) return '🏆 Tiefste Schicht erreicht';
    const target = s().mining.maxDepth + 1;
    const ore = ORE_BY_DEPTH[target]!;
    const haveElevator = (s().buildings['elevator'] ?? 0) >= target;
    return haveElevator
      ? `⛏️ Grabe zu ${ore.name} — $${format(cost)}`
      : `🛗 Aufzug Stufe ${target} nötig zum tieferen Graben`;
  });
  ctx.binder.disabled(depthBtn as HTMLButtonElement, () => !G.canUnlockDepth(s()));

  // Sell + autosell.
  const sellBtn = el('button', {
    class: 'op-btn sell-btn',
    text: '💰 Alles verkaufen',
    onClick: () => {
      const got = G.sellAll(s(), ctx.engine.derived(), ctx.bus);
      if (got > 0) ctx.sfx.play('sell');
    },
  });
  const autosell = el('label', { class: 'toggle' },
    el('input', { type: 'checkbox', onChange: (e) => (s().settings.autosell = (e.target as HTMLInputElement).checked) }),
    el('span', { text: 'Auto-Verkauf' }),
  );
  ctx.binder.effect(() => {
    const input = autosell.querySelector('input') as HTMLInputElement;
    if (input.checked !== s().settings.autosell) input.checked = s().settings.autosell;
  });

  // Bars.
  const energyBar = bar(ctx, '⚡ Energie', () => {
    const d = ctx.engine.derived();
    return {
      value: d.energyProduced,
      max: Math.max(d.energyConsumed, d.energyProduced, 1),
      color: d.throttle < 1 ? 'var(--bad)' : 'var(--energy)',
      text: `${format(d.energyProduced)} / ${format(d.energyConsumed)}${d.throttle < 1 ? ` (${Math.round(d.throttle * 100)}%)` : ''}`,
    };
  });
  const storageBar = bar(ctx, '📦 Lager', () => {
    const d = ctx.engine.derived();
    const focusOre = d.focusOreId;
    return { value: s().mining.ore[focusOre] ?? 0, max: d.storage, color: 'var(--accent)' };
  });

  // Clickable golden nugget that spawns over the mine.
  const nuggetEl = el('button', {
    class: 'nugget hidden',
    title: 'Klick das goldene Nugget!',
    onClick: () => { if (G.claimNugget(s(), ctx.bus)) ctx.sfx.play('reward'); },
  }, '💰');
  ctx.binder.effect(() => {
    const n = s().nugget;
    nuggetEl.classList.toggle('hidden', !n.active);
    if (n.active) {
      nuggetEl.style.left = `${n.x * 100}%`;
      nuggetEl.style.top = `${n.y * 100}%`;
      nuggetEl.textContent = NUGGET_BY_ID[n.kind]?.icon ?? '💰';
    }
  });

  return el(
    'div',
    { class: 'operation' },
    el('div', { class: 'canvas-wrap' }, canvas, nuggetEl),
    focusRow,
    mineBtn,
    comboMeter,
    abilityBar,
    el('div', { class: 'op-bars' }, energyBar, storageBar),
    depthBtn,
    el('div', { class: 'op-grid' }, toolBtn, sellBtn),
    el('div', { class: 'tool-line' }, toolName, autosell),
  );
}

// ───────────────────────────── Buildings ─────────────────────────────
export function panelBuildings(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' }, sectionTitle('Gebäude', 'Automatisiere und versorge deinen Betrieb'));
  for (const b of BUILDINGS) {
    root.appendChild(
      buyCard(ctx, {
        key: b.id,
        icon: b.icon,
        name: b.name,
        desc: b.description,
        owned: () => s().buildings[b.id] ?? 0,
        unlocked: () => G.buildingUnlocked(s(), b.id),
        cost: (amt) => G.buildingCost(s(), b.id, amt),
        maxAmount: () => G.maxBuildings(s(), b.id),
        buy: (amt) => G.buyBuilding(s(), b.id, amt, ctx.bus),
        info: () => buildingInfo(ctx, b.id),
      }),
    );
  }
  return root;
}

function buildingInfo(ctx: UIContext, id: string): string {
  const d = ctx.engine.derived();
  switch (id) {
    case 'drill': return `Mining: ${formatRate(d.miningRate)}`;
    case 'powerplant': return `Energy: +${format(d.energyProduced)}`;
    case 'smelter': return `Smelting: ${formatRate(d.smeltRate)}`;
    case 'lab': return `Research: ${formatRate(d.researchRate)}`;
    case 'warehouse': return `Capacity: ${format(d.storage)}`;
    case 'elevator': return `Shaft level: ${ctx.engine.state.buildings['elevator'] ?? 0}`;
    default: return '';
  }
}

// ───────────────────────────── Workers ─────────────────────────────
export function panelWorkers(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' }, sectionTitle('Belegschaft', 'Heuere Kumpel für passiven Ausstoß an'));
  for (const w of WORKERS) {
    root.appendChild(
      buyCard(ctx, {
        icon: w.icon,
        name: w.name,
        desc: w.description,
        owned: () => s().workers[w.id] ?? 0,
        unlocked: () => G.workerUnlocked(s(), w.id),
        cost: (amt) => G.workerCost(s(), w.id, amt),
        maxAmount: () => G.maxWorkers(s(), w.id),
        buy: (amt) => G.hireWorker(s(), w.id, amt, ctx.bus),
        info: () => `+${formatRate(w.rate)} each`,
      }),
    );
  }
  return root;
}

// ───────────────────────────── Upgrades ─────────────────────────────
export function panelUpgrades(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' }, sectionTitle('Upgrades', 'Einmalige dauerhafte Boosts'));
  for (const u of UPGRADES) {
    const card = buyCard(ctx, {
      icon: u.icon,
      name: u.name,
      desc: u.description,
      unlocked: () => G.upgradeUnlocked(s(), u.id),
      cost: () => u.cost,
      maxAmount: () => 1,
      buy: () => G.buyUpgrade(s(), u.id, ctx.bus),
      sfx: 'buy',
    });
    root.appendChild(card);
  }
  return root;
}

// ───────────────────────────── Market ─────────────────────────────
export function panelMarket(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' }, sectionTitle('Markt', 'Aktuelle Erz- & Barrenpreise'));
  for (const ore of ORES) {
    const price = el('span', { class: 'mk-price' });
    const oreQty = el('span', { class: 'mk-qty' });
    const ingotQty = el('span', { class: 'mk-qty' });
    const sellOreBtn = el('button', { class: 'mk-sell', text: 'Erz verk.', onClick: () => {
      if (G.sellOre(s(), ore.id, s().mining.ore[ore.id] ?? 0, ctx.engine.derived(), ctx.bus) > 0) ctx.sfx.play('sell');
    } });
    const sellIngotBtn = el('button', { class: 'mk-sell ingot', text: 'Barren verk.', onClick: () => {
      if (G.sellIngots(s(), ore.id, s().mining.ingots[ore.id] ?? 0, ctx.engine.derived(), ctx.bus) > 0) ctx.sfx.play('sell');
    } });

    const row = el('div', { class: 'mk-row' },
      el('span', { class: 'mk-name', text: `${ore.icon} ${ore.name}` }),
      price,
      el('div', { class: 'mk-stock' }, oreQty, sellOreBtn),
      el('div', { class: 'mk-stock' }, ingotQty, sellIngotBtn),
    );
    ctx.binder.toggle(row, 'hidden', () => s().mining.maxDepth < ore.depth);
    ctx.binder.text(price, () => {
      const p = s().market.price[ore.id] ?? ore.baseValue;
      const trend = p >= ore.baseValue ? '▲' : '▼';
      return `$${format(p)} ${trend}`;
    });
    ctx.binder.toggle(price, 'up', () => (s().market.price[ore.id] ?? 0) >= ore.baseValue);
    ctx.binder.text(oreQty, () => `${format(s().mining.ore[ore.id] ?? 0)} Erz`);
    ctx.binder.text(ingotQty, () => `${format(s().mining.ingots[ore.id] ?? 0)} Barren`);
    root.appendChild(row);
  }
  return root;
}

// ───────────────────────────── Research ─────────────────────────────
export function panelResearch(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const header = el('div', { class: 'research-points' });
  ctx.binder.text(header, () => `🔬 ${format(s().research.points)} Forschungspunkte  ·  ${formatRate(ctx.engine.derived().researchRate)}`);
  const root = el('div', { class: 'panel' }, sectionTitle('Forschung', 'Gib Punkte im Technologiebaum aus'), header);

  for (const t of TECHS) {
    const cost = el('span', { class: 'tech-cost' });
    const btn = el('button', { class: 'card tech-card', onClick: () => {
      if (G.researchTech(s(), t.id, ctx.bus)) { ctx.sfx.play('unlock'); ctx.requestRebuild(); }
      else ctx.sfx.play('error');
    } },
      el('div', { class: 'card-icon', text: t.icon }),
      el('div', { class: 'card-body' },
        el('div', { class: 'card-name', text: t.name }),
        el('div', { class: 'card-desc', text: t.description }),
        t.deps.length ? el('div', { class: 'tech-deps', text: `Benötigt: ${t.deps.join(', ')}` }) : null,
      ),
      el('div', { class: 'card-buy' }, cost),
    );
    ctx.binder.toggle(btn, 'hidden', () => !G.isTechUnlocked(s(), t.id) && !s().research.researched[t.id]);
    ctx.binder.toggle(btn, 'owned', () => !!s().research.researched[t.id]);
    ctx.binder.text(cost, () => (s().research.researched[t.id] ? '✓ Fertig' : `${format(t.cost)} Pkt`));
    ctx.binder.disabled(btn as HTMLButtonElement, () => !G.canResearch(s(), t.id));
    root.appendChild(btn);
  }
  return root;
}

// ───────────────────────────── Prestige ─────────────────────────────
export function panelPrestige(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const info = el('div', { class: 'prestige-info' });
  const gainEl = el('div', { class: 'prestige-gain' });
  const prestigeBtn = el('button', { class: 'prestige-btn', onClick: () => {
    const gain = G.prestigeGain(s());
    if (gain <= 0) { ctx.sfx.play('error'); return; }
    ctx.confirmPrestige(() => {
      if (G.doPrestige(s(), ctx.engine.clock.now(), ctx.bus)) { ctx.sfx.play('prestige'); ctx.requestRebuild(); }
    });
  } });
  ctx.binder.text(info, () => `✨ ${format(s().prestige.points)} Prestige-Punkte  ·  ${s().prestige.resets} Resets  ·  global ×${(1 + s().prestige.points * BALANCE.prestige.pointMult).toFixed(2)}`);
  ctx.binder.text(gainEl, () => {
    const g = G.prestigeGain(s());
    return g > 0 ? `Jetzt zurücksetzen für +${format(g)} Punkte` : `Erreiche $${format(BALANCE.prestige.minLifetime)} Gesamtverdienst für Prestige`;
  });
  ctx.binder.text(prestigeBtn, () => `🔄 Prestige (+${format(G.prestigeGain(s()))})`);
  ctx.binder.disabled(prestigeBtn as HTMLButtonElement, () => G.prestigeGain(s()) <= 0);

  const root = el('div', { class: 'panel' },
    sectionTitle('Prestige', 'Zurücksetzen für dauerhafte Kraft'), info, gainEl, prestigeBtn,
    sectionTitle('Fähigkeiten', 'Gib Prestige-Punkte aus'),
  );

  for (const sk of SKILLS) {
    const lvl = el('span', { class: 'card-owned' });
    const cost = el('span', { class: 'card-cost' });
    const btn = el('button', { class: 'card buy-card', onClick: () => {
      if (G.buySkill(s(), sk.id, ctx.bus)) ctx.sfx.play('buy'); else ctx.sfx.play('error');
    } },
      el('div', { class: 'card-icon', text: sk.icon }),
      el('div', { class: 'card-body' },
        el('div', { class: 'card-head' }, el('span', { class: 'card-name', text: sk.name }), lvl),
        el('div', { class: 'card-desc', text: sk.description }),
      ),
      el('div', { class: 'card-buy' }, cost),
    );
    ctx.binder.toggle(btn, 'hidden', () => !G.skillUnlocked(s(), sk.id));
    ctx.binder.text(lvl, () => `Lv.${s().prestige.skills[sk.id] ?? 0}/${sk.maxLevel}`);
    ctx.binder.text(cost, () => {
      const cur = s().prestige.skills[sk.id] ?? 0;
      return cur >= sk.maxLevel ? 'MAX' : `${format(G.skillCost(s(), sk.id))} Pkt`;
    });
    ctx.binder.disabled(btn as HTMLButtonElement, () => !G.canBuySkill(s(), sk.id));
    root.appendChild(btn);
  }
  return root;
}

// ───────────────────────────── Ascension ─────────────────────────────
export function panelAscension(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const info = el('div', { class: 'prestige-info' });
  const gainEl = el('div', { class: 'prestige-gain' });
  const ascendBtn = el('button', { class: 'ascend-btn', onClick: () => {
    if (G.ascensionGain(s()) <= 0) { ctx.sfx.play('error'); return; }
    ctx.confirmAscend(() => {
      if (G.doAscend(s(), ctx.engine.clock.now(), ctx.bus)) { ctx.sfx.play('prestige'); ctx.requestRebuild(); }
    });
  } });
  ctx.binder.text(info, () => `🌌 ${format(s().ascension.cores)} Kerne  ·  ${s().ascension.ascensions} Aufstiege  ·  global ×${(1 + s().ascension.cores * BALANCE.ascension.coreMult).toFixed(2)}`);
  ctx.binder.text(gainEl, () => {
    const g = G.ascensionGain(s());
    return g > 0 ? `Jetzt aufsteigen für +${format(g)} Kerne` : `Verdiene ${format(BALANCE.ascension.minPrestigePoints)} Gesamt-Prestige-Punkte zum Aufsteigen`;
  });
  ctx.binder.text(ascendBtn, () => `🌌 Aufsteigen (+${format(G.ascensionGain(s()))})`);
  ctx.binder.disabled(ascendBtn as HTMLButtonElement, () => G.ascensionGain(s()) <= 0);

  const root = el('div', { class: 'panel' },
    sectionTitle('Aufstieg', 'Transzendiere Prestige für dauerhafte Kerne'), info, gainEl, ascendBtn,
    sectionTitle('Kern-Perks', 'Gib Kerne aus — bleiben über Prestige hinweg'),
  );

  for (const p of ASCENSION_PERKS) {
    const lvl = el('span', { class: 'card-owned' });
    const cost = el('span', { class: 'card-cost' });
    const btn = el('button', { class: 'card buy-card ascend-perk', onClick: () => {
      if (G.buyPerk(s(), p.id, ctx.bus)) ctx.sfx.play('buy'); else ctx.sfx.play('error');
    } },
      el('div', { class: 'card-icon', text: p.icon }),
      el('div', { class: 'card-body' },
        el('div', { class: 'card-head' }, el('span', { class: 'card-name', text: p.name }), lvl),
        el('div', { class: 'card-desc', text: p.description }),
      ),
      el('div', { class: 'card-buy' }, cost),
    );
    ctx.binder.toggle(btn, 'hidden', () => !G.perkUnlocked(s(), p.id));
    ctx.binder.text(lvl, () => `Lv.${s().ascension.perks[p.id] ?? 0}/${p.maxLevel}`);
    ctx.binder.text(cost, () => {
      const cur = s().ascension.perks[p.id] ?? 0;
      return cur >= p.maxLevel ? 'MAX' : `${format(G.perkCost(s(), p.id))} ⬡`;
    });
    ctx.binder.disabled(btn as HTMLButtonElement, () => !G.canBuyPerk(s(), p.id));
    root.appendChild(btn);
  }
  return root;
}

// ───────────────────────────── Contracts (Quests) ─────────────────────────────
export function panelContracts(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const list = el('div', { class: 'contracts' });
  const empty = el('div', { class: 'empty-note', text: 'Keine aktiven Aufträge. Neue Angebote erscheinen mit dem Fortschritt.' });
  const root = el('div', { class: 'panel' }, sectionTitle('Aufträge', 'Liefere Erz für Belohnungen'), list, empty);

  let signature = '';
  const rebuild = (): void => {
    clear(list);
    for (const q of s().quests.active) {
      const oreName = ORES.find((o) => o.id === q.oreId)?.name ?? q.oreId;
      const progress = el('div', { class: 'q-progress' });
      const deliver = el('button', { class: 'q-deliver', text: 'Liefern', onClick: () => {
        if (G.completeQuest(s(), q.id, ctx.bus)) { ctx.sfx.play('reward'); rebuild(); }
        else ctx.sfx.play('error');
      } });
      const abandon = el('button', { class: 'q-abandon', text: '✕', title: 'Abbrechen', onClick: () => { G.abandonQuest(s(), q.id); rebuild(); } });
      const card = el('div', { class: 'q-card' },
        el('div', { class: 'q-head' }, el('span', { class: 'q-name', text: `Liefere ${format(q.amount)} ${oreName}` }), abandon),
        progress,
        el('div', { class: 'q-reward', text: `Belohnung: $${format(q.reward)}${q.researchReward ? ` · ${q.researchReward} Forschung` : ''}` }),
        deliver,
      );
      ctx.binder.text(progress, () => `${format(Math.min(s().mining.ore[q.oreId] ?? 0, q.amount))} / ${format(q.amount)}`);
      ctx.binder.disabled(deliver as HTMLButtonElement, () => !G.canCompleteQuest(s(), q.id));
      list.appendChild(card);
    }
    empty.classList.toggle('hidden', s().quests.active.length > 0);
  };
  rebuild();
  ctx.binder.effect(() => {
    const sig = s().quests.active.map((q) => q.id).join(',');
    if (sig !== signature) { signature = sig; rebuild(); }
  });
  ctx.bus.on('reset', rebuild);
  return root;
}

// ───────────────────────────── Trophies (Achievements) ─────────────────────────────
export function panelTrophies(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const grid = el('div', { class: 'trophy-grid' });
  const root = el('div', { class: 'panel' }, sectionTitle('Trophäen', 'Dauerhafte globale Boni'), grid);
  for (const a of ACHIEVEMENTS) {
    const cell = el('div', { class: 'trophy', title: a.description },
      el('div', { class: 'trophy-icon', text: a.icon }),
      el('div', { class: 'trophy-name', text: a.name }),
      el('div', { class: 'trophy-desc', text: a.description }),
      el('div', { class: 'trophy-reward', text: `×${a.reward}` }),
    );
    ctx.binder.toggle(cell, 'earned', () => !!s().achievements[a.id]);
    grid.appendChild(cell);
  }
  return root;
}

// ───────────────────────────── Stats ─────────────────────────────
export function panelStats(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const rows: Array<[string, () => string]> = [
    ['Geld', () => `$${fmt(ctx, s().economy.money)}`],
    ['Gesamtverdienst', () => `$${fmt(ctx, s().stats.lifetimeEarnings)}`],
    ['Erz insgesamt', () => format(s().stats.totalOreMined)],
    ['Klicks insgesamt', () => format(s().stats.totalClicks)],
    ['Tiefste Schicht', () => `${s().mining.maxDepth} (${ORE_BY_DEPTH[s().mining.maxDepth]?.name})`],
    ['Werkzeugstufe', () => `${s().mining.toolLevel}/${TOOLS.length}`],
    ['Prestiges', () => format(s().prestige.resets)],
    ['Trophäen', () => `${G.earnedCount(s())}/${ACHIEVEMENTS.length}`],
    ['Globaler Multiplikator', () => `×${format(ctx.engine.derived().mults.global)}`],
    ['Abbaurate', () => formatRate(ctx.engine.derived().miningRate)],
    ['Spielzeit', () => formatDuration(s().meta.playtimeMs / 1000)],
  ];
  const root = el('div', { class: 'panel' },
    sectionTitle('Verläufe'),
    sparkline(ctx, '📈 Einkommen / s', () => ctx.history.income, 'var(--gold)'),
    sparkline(ctx, '🪙 Fokus-Preis', () => ctx.history.price, 'var(--gem)'),
    sectionTitle('Statistik'),
  );
  for (const [label, get] of rows) {
    const val = el('span', { class: 'stat-val' });
    ctx.binder.text(val, get);
    root.appendChild(el('div', { class: 'stat-row' }, el('span', { class: 'stat-label', text: label }), val));
  }

  // Multiplier breakdown — lists every source feeding the global/channel mults.
  root.appendChild(sectionTitle('Multiplikator-Aufschlüsselung'));
  const breakdown = el('div', { class: 'breakdown' });
  root.appendChild(breakdown);
  let sig = '';
  ctx.binder.effect(() => {
    if (root.classList.contains('hidden')) return; // opt-in: only when Stats is open
    const { sources } = G.computeMultiplierBreakdown(s());
    const next = sources.map((x) => `${x.label}${x.factor.toFixed(3)}`).join('|');
    if (next === sig) return;
    sig = next;
    clear(breakdown);
    if (sources.length === 0) {
      breakdown.appendChild(el('div', { class: 'empty-note', text: 'Noch keine Multiplikatoren — kaufe Upgrades & forsche!' }));
      return;
    }
    for (const src of sources) {
      breakdown.appendChild(el('div', { class: 'bd-row' },
        el('span', { class: 'bd-chan', text: src.channel }),
        el('span', { class: 'bd-label', text: src.label }),
        el('span', { class: 'bd-factor', text: `×${src.factor < 10 ? src.factor.toFixed(2) : format(src.factor)}` }),
      ));
    }
  });
  return root;
}

// ───────────────────────────── Mastery ─────────────────────────────
export function panelMastery(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' },
    sectionTitle('Erz-Meisterung', `+${Math.round(MASTERY.bonusPerTier * 100)}% Abbau pro Stufe, pro Erz`));
  for (const ore of ORES) {
    const tierEl = el('span', { class: 'card-owned' });
    const bonusEl = el('div', { class: 'card-info' });
    const fill = el('div', { class: 'goal-bar-fill' });
    const prog = el('div', { class: 'mastery-prog' });
    const row = el('div', { class: 'card mastery-card' },
      el('div', { class: 'card-icon', text: ore.icon }),
      el('div', { class: 'card-body' },
        el('div', { class: 'card-head' }, el('span', { class: 'card-name', text: ore.name }), tierEl),
        bonusEl,
        el('div', { class: 'goal-bar' }, fill),
        prog,
      ),
    );
    ctx.binder.toggle(row, 'hidden', () => s().mining.maxDepth < ore.depth);
    ctx.binder.effect(() => {
      const tier = G.oreMasteryTier(s(), ore.id);
      tierEl.textContent = `Stufe ${tier}/${MAX_MASTERY_TIER}`;
      bonusEl.textContent = `Abbau ×${G.oreMasteryMult(s(), ore.id).toFixed(2)}`;
      const mined = s().stats.oreMined[ore.id] ?? 0;
      const next = G.nextMasteryThreshold(s(), ore.id);
      if (next === null) {
        fill.style.width = '100%';
        prog.textContent = `${format(mined)} abgebaut · MAX`;
      } else {
        const prev = tier > 0 ? MASTERY.thresholds[tier - 1]! : 0;
        fill.style.width = `${Math.max(0, Math.min(100, ((mined - prev) / (next - prev)) * 100))}%`;
        prog.textContent = `${format(mined)} / ${format(next)} abgebaut`;
      }
    });
    root.appendChild(row);
  }
  return root;
}

// ───────────────────────────── Challenges ─────────────────────────────
export function panelChallenges(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' },
    sectionTitle('Herausforderungen', 'Nimm ein Handicap an, erreiche das Ziel, behalte eine dauerhafte Belohnung'));
  for (const c of CHALLENGES) {
    const status = el('div', { class: 'ch-status' });
    const prog = el('div', { class: 'q-progress' });
    const fill = el('div', { class: 'goal-bar-fill' });
    const bar = el('div', { class: 'goal-bar' }, fill);
    const startBtn = el('button', { class: 'ch-start', text: 'Starten', onClick: () => {
      if (G.startChallenge(s(), c.id, ctx.bus)) ctx.sfx.play('unlock'); else ctx.sfx.play('error');
    } });
    const abandonBtn = el('button', { class: 'q-abandon', text: '✕ Abbrechen', onClick: () => G.abandonChallenge(s()) });
    const card = el('div', { class: 'card ch-card' },
      el('div', { class: 'card-icon', text: c.icon }),
      el('div', { class: 'card-body' },
        el('div', { class: 'card-head' }, el('span', { class: 'card-name', text: c.name }), status),
        el('div', { class: 'card-desc', text: c.description }),
        el('div', { class: 'ch-reward', text: `Belohnung: ×${c.reward.value} ${c.reward.type.replace('Mult', '')}` }),
        bar, prog,
        el('div', { class: 'ch-actions' }, startBtn, abandonBtn),
      ),
    );
    ctx.binder.effect(() => {
      const st = s();
      const done = st.challenges.completed.includes(c.id);
      const active = st.challenges.activeId === c.id;
      const unlocked = G.challengeUnlocked(st, c.id);
      card.classList.toggle('owned', done);
      status.textContent = done ? '✓ Gemeistert' : active ? 'AKTIV' : unlocked ? '' : '🔒 Gesperrt';
      startBtn.classList.toggle('hidden', done || active || !unlocked || st.challenges.activeId !== null);
      abandonBtn.classList.toggle('hidden', !active);
      bar.classList.toggle('hidden', !active);
      prog.classList.toggle('hidden', !active);
      if (active) {
        const p = G.challengeProgress(st);
        fill.style.width = `${Math.max(0, Math.min(100, (p / c.target) * 100))}%`;
        prog.textContent = `$${format(p)} / $${format(c.target)} verdient`;
      }
    });
    root.appendChild(card);
  }
  return root;
}

// ───────────────────────────── Automation ─────────────────────────────
export function panelAutomation(ctx: UIContext): HTMLElement {
  const s = () => ctx.engine.state;
  const root = el('div', { class: 'panel' },
    sectionTitle('Automatik', 'Manager, die deinen Betrieb für dich führen'));

  const toggleRow = (
    label: string, desc: string,
    get: () => boolean, set: (v: boolean) => void,
    unlocked: () => boolean, lockHint: string,
    extra?: HTMLElement,
  ): void => {
    const input = el('input', { type: 'checkbox', onChange: (e) => set((e.target as HTMLInputElement).checked) }) as HTMLInputElement;
    const lock = el('span', { class: 'auto-lock', text: lockHint });
    const row = el('div', { class: 'auto-row' },
      el('label', { class: 'toggle' }, input, el('span', { text: label })),
      el('div', { class: 'auto-desc', text: desc }),
      lock,
      extra ?? null,
    );
    ctx.binder.effect(() => {
      const ok = unlocked();
      input.disabled = !ok;
      lock.classList.toggle('hidden', ok);
      if (extra) extra.classList.toggle('hidden', !ok);
      if (input.checked !== get()) input.checked = get();
    });
    root.appendChild(row);
  };

  const threshold = (get: () => number, set: (v: number) => void, suffix: string): HTMLElement => {
    const input = el('input', { type: 'number', class: 'auto-threshold', onChange: (e) => {
      const v = parseFloat((e.target as HTMLInputElement).value);
      if (Number.isFinite(v) && v >= 1) set(v);
    } }) as HTMLInputElement;
    input.value = String(get());
    return el('label', { class: 'auto-threshold-row' }, el('span', { text: `Min. ${suffix}: ` }), input);
  };

  toggleRow('Nuggets automatisch einsammeln', 'Klickt goldene Nuggets sofort an, wenn sie erscheinen.',
    () => s().automation.autoNugget, (v) => (s().automation.autoNugget = v),
    () => s().stats.nuggetsClicked >= 1, '🔒 Klick zuerst ein goldenes Nugget');

  toggleRow('Auto-Kauf', 'Kauft laufend Upgrades und das günstigste leistbare Gebäude/den günstigsten Arbeiter.',
    () => s().automation.autoBuy, (v) => (s().automation.autoBuy = v),
    () => s().stats.lifetimeEarnings >= 5e4, '🔒 Verdiene insgesamt 50.000 $');

  toggleRow('Auto-Prestige', 'Macht automatisch Prestige, sobald der Gewinn deine Schwelle erreicht.',
    () => s().automation.autoPrestige, (v) => (s().automation.autoPrestige = v),
    () => s().prestige.resets >= 1, '🔒 Mach zuerst einmal Prestige',
    threshold(() => s().automation.autoPrestigeThreshold, (v) => (s().automation.autoPrestigeThreshold = v), 'Punkte'));

  toggleRow('Auto-Aufstieg', 'Steigt automatisch auf, sobald der Kern-Gewinn deine Schwelle erreicht.',
    () => s().automation.autoAscend, (v) => (s().automation.autoAscend = v),
    () => s().ascension.ascensions >= 1, '🔒 Steig zuerst einmal auf',
    threshold(() => s().automation.autoAscendThreshold, (v) => (s().automation.autoAscendThreshold = v), 'Kerne'));

  return root;
}

// ───────────────────────────── Codex ─────────────────────────────
const CODEX: Array<[string, string, string]> = [
  ['⛏️', 'Abbau', 'Tippe ABBAU, um die fokussierte Schicht zu graben. Klick-Ketten bauen einen Combo-Multiplikator auf. Auto-Bohrer und Arbeiter bauen für dich ab — sogar offline (bis zu 8h).'],
  ['💰', 'Verkauf & Markt', 'Verkaufe Roherz oder veredelte Barren am Markt. Preise schwanken, also verkaufe bei Spitzen. Barren sind ein Vielfaches des Rohwerts wert.'],
  ['⚡', 'Energie', 'Maschinen und manche Arbeiter verbrauchen Energie; Kraftwerke erzeugen sie. Übersteigt der Bedarf das Angebot, drosseln alle Maschinen — halte das Netz voraus.'],
  ['🕳️', 'Tiefe', 'Bau Aufzugschächte und grab tiefer für reicheres Erz. Tiefere Schichten sind viel wertvoller, aber härter — halte Bohrer/Werkzeug aktuell.'],
  ['🔬', 'Forschung', 'Labore erzeugen Forschungspunkte für den Technologiebaum — dauerhafte Multiplikatoren und Freischaltungen.'],
  ['⚡', 'Fähigkeiten & Nuggets', 'Löse Fähigkeiten für große zeitlich begrenzte Boosts aus (Abklingzeit beachten). Klick goldene Nuggets sofort an für Geld, Erz oder Buffs.'],
  ['✨', 'Prestige', 'Setze deinen Lauf zurück für Prestige-Punkte und einen bleibenden Fähigkeitenbaum. Jeder Punkt verstärkt alles global.'],
  ['🌌', 'Aufstieg', 'Sobald du tiefen Prestige-Fortschritt hast, steig für Kerne und einen Perk-Baum auf, der Prestige übersteht — das Langzeitspiel.'],
];

export function panelCodex(ctx: UIContext): HTMLElement {
  void ctx;
  const root = el('div', { class: 'panel' }, sectionTitle('Kodex', 'Wie alles funktioniert'));
  for (const [icon, title, body] of CODEX) {
    root.appendChild(
      el('div', { class: 'codex-entry' },
        el('div', { class: 'codex-icon', text: icon }),
        el('div', { class: 'codex-body' },
          el('div', { class: 'codex-title', text: title }),
          el('div', { class: 'codex-text', text: body }),
        ),
      ),
    );
  }
  return root;
}

// ───────────────────────────── helpers ─────────────────────────────
function canvasBurst(canvas: HTMLCanvasElement, e: MouseEvent): void {
  const api = (canvas as any).__mine as { burstAt?: (x: number, y: number) => void } | undefined;
  if (api?.burstAt) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return; // not laid out yet
    api.burstAt(e.clientX - rect.left, e.clientY - rect.top);
  }
}

